export type SelectThreadHandler = (threadId?: string) => void;
export type ResolveCommentIds = {
	contentId: string | undefined;
	referenceId: string | undefined;
};
export type ResolvedCommentHandler = (resolvedIds: ResolveCommentIds) => void;

export class ContentTypesCommentService {
	private selectThreadHandler: SelectThreadHandler | undefined;
	private reopenResolvedCommentHandler: ResolvedCommentHandler | undefined;
	private showPanelHandler: ((threadId?: string) => void) | undefined;
	private hidePanelHandler: (() => void) | undefined;
	private selectCommentThreadHandler: ((threadId?: string) => void) | undefined;
	private onDeleteCommentHandler: ((threadKey: string) => void) | undefined;
	private triggerCommentPanelRefreshHandler: (() => Promise<void>) | undefined;
	// When a refresh is requested while the comments panel is unmounted (no handler
	// registered), we set this flag so the next registration triggers a refetch.
	private hasPendingCommentPanelRefresh: boolean = false;
	private onUpdateCommentHandler:
		| ((threadKey: string, actionType?: 'reply' | 'edit') => void)
		| undefined;
	private onResolveCommentHandler: ((threadKey: string) => void) | undefined;
	private onUpdateReactionHandler:
		| ((threadKey: string, emojiId: string, actionType: 'add' | 'delete') => Promise<void>)
		| undefined;
	private onUpdateUnreadCommentsHandler:
		| ((unreadComments: string[], read: boolean) => void)
		| undefined;
	private triggerReadStatusCommentHandler:
		| ((unreadComments: string[], read: boolean) => void)
		| undefined;

	constructor() {
		this.selectThreadHandler = undefined;
		this.reopenResolvedCommentHandler = undefined;
		this.showPanelHandler = undefined;
		this.selectCommentThreadHandler = undefined;
		this.hidePanelHandler = undefined;
		this.onDeleteCommentHandler = undefined;
		this.onUpdateCommentHandler = undefined;
		this.triggerCommentPanelRefreshHandler = undefined;
		this.onResolveCommentHandler = undefined;
		this.onUpdateReactionHandler = undefined;
		this.hasPendingCommentPanelRefresh = false;
		this.onUpdateUnreadCommentsHandler = undefined;
		this.triggerReadStatusCommentHandler = undefined;
	}

	dispose() {
		this.selectThreadHandler = undefined;
		this.reopenResolvedCommentHandler = undefined;
		this.showPanelHandler = undefined;
		this.selectCommentThreadHandler = undefined;
		this.hidePanelHandler = undefined;
		this.onDeleteCommentHandler = undefined;
		this.triggerCommentPanelRefreshHandler = undefined;
		this.onUpdateCommentHandler = undefined;
		this.onResolveCommentHandler = undefined;
		this.onUpdateReactionHandler = undefined;
		this.onUpdateUnreadCommentsHandler = undefined;
		this.triggerReadStatusCommentHandler = undefined;
	}

	/**
	 * Register a handler for thread selection events
	 */
	registerSelectThreadHandler(handler?: SelectThreadHandler) {
		this.selectThreadHandler = handler;
	}

	/**
	 * Handle thread selection events
	 */
	handleSelectThread(threadId?: string) {
		this.selectThreadHandler?.(threadId);
	}

	/**
	 * Register a handler for show panel events
	 */
	registerShowPanelHandler(handler?: () => void) {
		this.showPanelHandler = handler;
	}

	/**
	 * Show the comments panel
	 */
	showPanel(threadId?: string) {
		this.showPanelHandler?.(threadId);
	}

	/**
	 * Register a handler for hide panel events
	 */
	registerHidePanelHandler(handler?: () => void) {
		this.hidePanelHandler = handler;
	}

	/**
	 * Hide the comments panel
	 */
	hidePanel() {
		this.hidePanelHandler?.();
	}

	/**
	 * Register a handler for select comment thread events
	 */
	registerSelectCommentThreadHandler(handler?: (threadId?: string) => void) {
		this.selectCommentThreadHandler = handler;
	}

	/**
	 * Select a specific comment thread
	 */
	selectCommentThread(threadId?: string) {
		this.selectCommentThreadHandler?.(threadId);
	}

	/**
	 * This is called in databases to register the callback confluence FE
	 * will call after a resolved comment has been reopened
	 */
	registerOnReopenResolvedComment(callback?: ResolvedCommentHandler): void {
		this.reopenResolvedCommentHandler = callback;
	}

	/**
	 * This will be called in confluence FE after we have successfully
	 * reopened a resolved comment. This allows databases to re-fetch and re-render
	 * the comment
	 */
	onReopenResolvedComment(commentIds: ResolveCommentIds) {
		this.reopenResolvedCommentHandler?.(commentIds);
	}

	/**
	 * Register a handler for delete comment events (called by content types)
	 */
	registerOnDeleteComment(callback?: (threadKey: string) => void) {
		this.onDeleteCommentHandler = callback;
	}

	/**
	 * Handle a delete comment event (fired by CF)
	 */
	onDeleteComment(threadKey: string) {
		this.onDeleteCommentHandler?.(threadKey);
	}

	/**
	 * Register a handler for update comment events (called by content types)
	 */
	registerOnUpdateComment(callback?: (threadKey: string, actionType?: 'reply' | 'edit') => void) {
		this.onUpdateCommentHandler = callback;
	}

	/**
	 * Handle an update comment event (fired by CF)
	 * @param threadKey - The thread key of the comment
	 * @param actionType - Optional action type: 'reply' for new replies, 'edit' for comment edits
	 */
	onUpdateComment(threadKey: string, actionType: 'reply' | 'edit') {
		this.onUpdateCommentHandler?.(threadKey, actionType);
	}

	/**
	 * Register a handler for trigger comment panel refresh events (called by content types)
	 */
	registerTriggerCommentPanelRefresh(handler?: () => Promise<any>) {
		this.triggerCommentPanelRefreshHandler = handler;
		// If a refresh was requested while no handler was registered, trigger it now
		// so the newly mounted view refetches immediately.
		if (handler && this.hasPendingCommentPanelRefresh) {
			this.hasPendingCommentPanelRefresh = false;
			void handler();
		}
	}

	/**
	 * Register a handler for resolve comment events (called by content types)
	 */
	registerOnResolveComment(callback?: (threadKey: string) => void) {
		this.onResolveCommentHandler = callback;
	}

	/**
	 * Handle a delete comment event (fired by CF)
	 */
	onResolveComment(threadKey: string) {
		this.onResolveCommentHandler?.(threadKey);
	}

	/**
	 * Trigger a refresh of the comment panel
	 */
	async triggerCommentPanelRefresh() {
		if (this.triggerCommentPanelRefreshHandler) {
			await this.triggerCommentPanelRefreshHandler();
			return;
		}
		// No handler registered means the panel is likely closed/unmounted.
		// Remember to refresh on next registration.
		this.hasPendingCommentPanelRefresh = true;
	}

	/**
	 * Register a handler for update reaction events (called by content types)
	 * @param callback - A callback function that will be called when a reaction is updated
	 */
	registerOnUpdateReaction(
		callback: (threadKey: string, emojiId: string, actionType: 'add' | 'delete') => Promise<void>,
	) {
		this.onUpdateReactionHandler = callback;
	}

	/**
	 * Handle an update reaction event (fired by CF)
	 * @param threadKey - The thread key of the comment
	 * @param emojiId - The emoji ID of the reaction
	 * @param actionType - The action type of the reaction
	 * @returns
	 */
	onUpdateReaction(threadKey: string, emojiId: string, actionType: 'add' | 'delete') {
		return this.onUpdateReactionHandler?.(threadKey, emojiId, actionType);
	}

	/**
	 * Register a handler for update unread comments events (called by content types)
	 * @param handler - A callback function that will be called by content types (whiteboards, databases) to register a handler that CFE can use
	 * to notify about the comment being read (e.g. update the unread comments status).
	 */
	registerOnUpdateUnreadComments(handler?: () => void) {
		this.onUpdateUnreadCommentsHandler = handler;
	}

	/**
	 * Handle an update unread comments event (fired by CF)
	 * CFE -> Content Types (whiteboards, databases)
	 * @param commentIds - The IDs of the comments that have been read
	 * @param read - Whether the comments have been read or unread
	 * @see triggerReadStatusComment for the counterpart method
	 */
	onUpdateUnreadComments(commentIds: string[], read: boolean = false) {
		return this.onUpdateUnreadCommentsHandler?.(commentIds, read);
	}

	/**
	 * Register a handler for trigger read status comment events (called by CF)
	 * @param handler - A callback function used by CFE to register a handler that can be used when content types notifies CFE about a comment being read.
	 */
	registerTriggerReadStatusComment(handler?: (commentIds: string[], read: boolean) => void) {
		this.triggerReadStatusCommentHandler = handler;
	}

	/**
	 * Triggers an update of the unread comments status (fired by content types)
	 * Content Types (whiteboards, databases) -> CFE
	 * @param commentIds - The IDs of the comments that have been read
	 * @param read - Whether the comments have been read or unread.
	 * @see onUpdateUnreadComments for the counterpart method
	 */
	triggerReadStatusComment(commentIds: string[], read: boolean = false) {
		return this.triggerReadStatusCommentHandler?.(commentIds, read);
	}
}
